/* v8 ignore start */
import map from 'lodash/map';
import get from 'lodash/get';
import reduce from 'lodash/reduce';
import { call, all, fork, takeLatest, put, select } from 'redux-saga/effects';
import { convertDatePickerTimeToApiTime,  } from 'helpers/timeFormat';
import { promisifyModel } from 'helpers/apiClient';

import {
  name,
  handleClose,
  handleSavingState
} from './BulkEdition.slice';

import {
  handlePreviousView,
  handleViewChange
} from 'containers/BaseWizard/BaseWizard.slice';

const getSelectedWidgets = (state) => state.BulkEdition.selectedWidgets;
export const getWidgetSettings = (state) => state.BulkEdition.widgetSettings;
const getBulkEditionMode = (state) => state.BulkEdition.isBulkEditionMode;
export const getTimezone = (state) => state.AppUser.user.timezone;

export const triggerBackboneSelectWidget = function* () {
  const selectedWidgets = yield select(getSelectedWidgets);
  // eslint-disable-next-line no-negated-condition
  Vent && Vent.trigger('bulk_edition_mode:selection', selectedWidgets);
};

export const triggerBackboneEditModeToggle = function* () {
  const bulkEditMode = yield select(getBulkEditionMode);
  // eslint-disable-next-line no-negated-condition
  Vent && Vent.trigger('bulk_edition_mode:toggle', bulkEditMode);
};

export const triggerBackboneExitBulkEditionMode = function* () {
  const bulkEditMode = yield select(getBulkEditionMode);
  // eslint-disable-next-line no-negated-condition
  Vent && Vent.trigger('bulk_edition_mode:toggle', bulkEditMode);
};

export const selectwidgetSaga = function* () {
  yield takeLatest(`${name}/selectWidget`, triggerBackboneSelectWidget);
};

export const unselectWidgettwidgetSaga = function* () {
  yield takeLatest(`${name}/unselectWidget`, triggerBackboneSelectWidget);
};

export const toggleBulkEditionModeSaga = function* () {
  yield takeLatest(`${name}/toggleBulkEditionMode`, triggerBackboneEditModeToggle);
};

export const toggleExitBulkEditionModeSaga = function* () {
  yield takeLatest(`${name}/exitBulkEditionMode`, triggerBackboneExitBulkEditionMode);
};

// reset active index to 0 if RESET_PATH actions is used
export const backButton = function* () {
  yield put(handlePreviousView());
};

export const backButtonSaga = function* () {
  yield takeLatest(`${name}/backButton`, backButton);
};

// reset active index to 0 if RESET_PATH actions is used
export const handleCloseAction = function* () {
  yield put(handleViewChange(0));
};

export const _formatBulkSettings = (allWidgetSettings, profileOptions, timezone) => {
  return reduce(allWidgetSettings, (acc, setting, key) => {
    const { value, selected, doNotDisplay, includeAll } = setting;
    if (selected) {
      acc[key] = value;
      if (key === 'widget_filter') {
        acc.hide_filter = doNotDisplay;
      }
      if (key === 'candidate') {
        acc[key] = value ? acc[key] : undefined;
      }
      if (key === 'candidates') {
        delete acc[key]; // widgets actually use 'select_profiles' for this
        if (includeAll ) {
          acc.selected_profiles = null;
        } else {
          const profiles = profileOptions.reduce((arr, { value: id, label }) => {
            if (value.includes(id)) {
              arr.push({ id, name: label });
            }
            return arr;
          }, []);
          acc.selected_profiles = profiles.length ? profiles : null;
        }
      }
      if (key === 'dateRange_option') {
        // this is the condition when the user clears the daterange picker
        if (value === null) {
          acc.isDateRangeRequired = false;
        } else {
          acc.hide_dateRange = doNotDisplay;
          const { timeStartWindow, startDate, endDate } = value;
          const start = convertDatePickerTimeToApiTime(startDate, timezone);
          const end = endDate === 'now' ? endDate : convertDatePickerTimeToApiTime(endDate, timezone);
          let rangeName = timeStartWindow === false ? 'custom' : timeStartWindow;
          rangeName = rangeName || 'last 30 days';
          acc[key] = {
            name: rangeName,
            start_time: start,
            end_time: end
          };
        }
      }
    }
    return acc;
  }, {});
};

export const _applyBulkSettings = (model, bulkSettings) => {
  const settings = { ...bulkSettings };
  const defaults = model._defaults;
  if (settings.hasOwnProperty('title')) {
    settings.title = settings.title || defaults.title;
  }
  if (!defaults.has_filter_selector) {
    delete settings.hide_filter;
    delete settings.widget_filter;
  }
  if (!defaults.has_dateRange_selector) {
    delete settings.hide_dateRange;
    delete settings.dateRange_option;
    delete settings.isDateRangeRequired;
  }
  if (!defaults.has_profile_selector && !defaults.has_key_messages_selector_container) {
    delete settings.candidate;
  }
  if (settings.hasOwnProperty('selected_profiles')) {
    if (!defaults.has_included_profiles && !defaults.has_key_messages_selector_container) {
      delete settings.selected_profiles;
      delete settings.candidates;
    }
  }
  Object.assign(model.attributes, settings);
};

// save changes
export const handleApplySettings = function* ({payload: [dashboardModel, profileOptions]}) {
  try {
    yield put(handleSavingState());
    const timezone = yield select(getTimezone);
    const dashboardWidgets = get(dashboardModel, 'attributes.widgets', []);
    const selectedWidgets = yield select(getSelectedWidgets);
    const widgetIds = map(selectedWidgets, 'id');
    const allWidgetSettings = yield select(getWidgetSettings);
    const bulkSettings = _formatBulkSettings(allWidgetSettings, profileOptions, timezone);
    dashboardWidgets.forEach((model) => {
      if (widgetIds.includes(model.id)) {
        _applyBulkSettings(model, bulkSettings);
      }
    });
    yield call(promisifyModel, dashboardModel);
    Vent.trigger('change:dashboard', dashboardModel);
    Account.toastrNotifications._successMessage({
      success_title: '',
      success_message: "Your widgets' settings have been updated!"
    });
    yield put(handleClose());
  } catch(err) {
    Account.toastrNotifications._errorMessage({
      error_title: '',
      error_message: "There was a problem updating your widgets' settings."
    });
    yield put(handleClose());
  }
};

export const handleCloseSaga = function* () {
  yield takeLatest(`${name}/handleClose`, handleCloseAction);
};

export const handleApplySettingsSaga = function* () {
  yield takeLatest(`${name}/applySettings`, handleApplySettings);
};

const sagas = function* () {
  yield all([
    fork(backButtonSaga),
    fork(handleCloseSaga),
    fork(selectwidgetSaga),
    fork(unselectWidgettwidgetSaga),
    fork(toggleBulkEditionModeSaga),
    fork(toggleExitBulkEditionModeSaga),
    fork(handleApplySettingsSaga)
  ]);
};

export default sagas;
export {
  getSelectedWidgets,
  getBulkEditionMode
};

